import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tab,
  Tabs,
  Badge,
  IconButton,
  Tooltip,
  Snackbar,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { classAPI } from '../../services/api';
import { Class } from '../../types';

interface ClassAttendee {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  status: 'confirmed' | 'cancelled' | 'attended' | 'no_show' | 'waitlist';
  paid: boolean;
  paidAmount?: number;
  waitlistPosition?: number;
  notes?: string;
  createdAt: string;
  memberProfile?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    membershipTier?: {
      id: string;
      name: string;
    };
  };
}

interface ClassAttendeesData {
  confirmed: ClassAttendee[];
  waitlist: ClassAttendee[];
  attendeeCount: number;
  waitlistCount: number;
}

interface ClassAttendeesDialogProps {
  open: boolean;
  onClose: () => void;
  classItem: Class | null;
  onSuccess?: (message?: string) => void;
}

const ClassAttendeesDialog: React.FC<ClassAttendeesDialogProps> = ({
  open,
  onClose,
  classItem,
  onSuccess
}) => {
  const [attendeesData, setAttendeesData] = useState<ClassAttendeesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Confirmation dialog state
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [attendeeToCancel, setAttendeeToCancel] = useState<ClassAttendee | null>(null);
  const [cancelTransactionChoice, setCancelTransactionChoice] = useState<boolean>(true); // Default to cancelling transaction

  // Fetch attendees data when dialog opens
  useEffect(() => {
    if (open && classItem) {
      fetchAttendees();
    }
  }, [open, classItem]);

  const fetchAttendees = async () => {
    if (!classItem) return;

    try {
      setLoading(true);
      setError(null);
      const response = await classAPI.getClassBookings(classItem.id);
      setAttendeesData(response.data);
    } catch (error: any) {
      console.error('Error fetching attendees:', error);
      setError('Failed to fetch class attendees');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (attendee: ClassAttendee) => {
    if (!classItem) return;

    try {
      await classAPI.cancelMemberBooking(classItem.id, attendee.id, cancelTransactionChoice);
      
      const message = cancelTransactionChoice
        ? `Cancelled ${attendee.userName || attendee.userEmail}'s booking (transaction cancelled)`
        : `Cancelled ${attendee.userName || attendee.userEmail}'s booking (member will be charged)`;
      
      setSuccessMessage(message);
      
      // Refresh the attendees list
      await fetchAttendees();
      
      // Notify parent to refresh class list without showing another success message
      if (onSuccess) {
        onSuccess(); // Don't pass a message for cancellation
      }
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      setError(error.response?.data?.message || 'Failed to cancel booking');
    } finally {
      // Close confirmation dialog
      setConfirmationOpen(false);
      setAttendeeToCancel(null);
      setCancelTransactionChoice(true); // Reset to default
    }
  };

  const handleShowCancelConfirmation = (attendee: ClassAttendee) => {
    setAttendeeToCancel(attendee);
    setCancelTransactionChoice(true); // Reset to default (cancel transaction)
    setConfirmationOpen(true);
  };

  const handleConfirmCancel = () => {
    if (attendeeToCancel) {
      handleCancelBooking(attendeeToCancel);
    }
  };

  const isClassInFuture = (): boolean => {
    if (!classItem) return false;
    
    try {
      // Use improved date/time parsing similar to what we fixed in MemberClassesPage
      const timeOnly = classItem.startTime.split('+')[0].split('-')[0]; // Remove timezone if present
      const classDateTime = new Date(`${classItem.scheduleDate}T${timeOnly}`);
      
      // If parsing failed, try alternative approach
      if (isNaN(classDateTime.getTime())) {
        const date = new Date(classItem.scheduleDate);
        const timeParts = timeOnly.split(':');
        date.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]), parseInt(timeParts[2] || '0'));
        return date > new Date();
      }
      
      return classDateTime > new Date();
    } catch (error) {
      console.error('Error parsing class date/time in ClassAttendeesDialog:', error);
      return false;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'waitlist':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'attended':
        return 'primary';
      case 'no_show':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (attendee: ClassAttendee) => {
    if (attendee.status === 'waitlist') {
      return `Waitlist #${attendee.waitlistPosition}`;
    }
    return attendee.status.charAt(0).toUpperCase() + attendee.status.slice(1);
  };

  const renderAttendeesList = (attendees: ClassAttendee[]) => (
    <List sx={{ width: '100%' }}>
      {attendees.map((attendee, index) => (
        <React.Fragment key={attendee.id}>
          <ListItem 
            alignItems="flex-start" 
            sx={{ py: 2 }}
            secondaryAction={
              isClassInFuture() && attendee.status !== 'cancelled' ? (
                <Tooltip title="Cancel booking">
                  <IconButton 
                    edge="end" 
                    aria-label="cancel"
                    onClick={() => handleShowCancelConfirmation(attendee)}
                    color="error"
                    size="small"
                  >
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
              ) : null
            }
          >
            <ListItemAvatar>
              <Avatar>
                <PersonIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    {attendee.memberProfile?.firstName && attendee.memberProfile?.lastName 
                      ? `${attendee.memberProfile.firstName} ${attendee.memberProfile.lastName}`
                      : attendee.userName
                    }
                  </Typography>
                  <Chip
                    size="small"
                    label={getStatusLabel(attendee)}
                    color={getStatusColor(attendee.status) as any}
                    variant="outlined"
                  />
                  {attendee.paid && (
                    <Chip
                      size="small"
                      label="Paid"
                      color="success"
                      variant="filled"
                      icon={<CheckCircleIcon />}
                    />
                  )}
                </Box>
              }
              secondary={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {attendee.memberProfile?.email || attendee.userEmail}
                    </Typography>
                  </Box>
                  
                  {attendee.memberProfile?.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {attendee.memberProfile.phone}
                      </Typography>
                    </Box>
                  )}

                  {attendee.memberProfile?.membershipTier && (
                    <Typography variant="body2" color="text.secondary">
                      Membership: {attendee.memberProfile.membershipTier.name}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Registered: {new Date(attendee.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {attendee.paidAmount && (
                    <Typography variant="body2" color="text.secondary">
                      Amount: ${attendee.paidAmount}
                    </Typography>
                  )}

                  {attendee.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Notes: {attendee.notes}
                    </Typography>
                  )}
                </Box>
              }
            />
          </ListItem>
          {index < attendees.length - 1 && <Divider variant="inset" component="li" />}
        </React.Fragment>
      ))}
    </List>
  );

  if (!classItem) return null;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown={true}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '70vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {classItem.name} - Attendees
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {classItem.instructorName || classItem.instructor} • {new Date(classItem.scheduleDate).toLocaleDateString()}
            </Typography>
          </Box>
          <Tooltip title="Close">
            <IconButton onClick={onClose}>
              <CancelIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : attendeesData ? (
          <Box>
            {/* Tabs for confirmed vs waitlist */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab
                  label={
                    <Badge badgeContent={attendeesData.attendeeCount} color="success">
                      Confirmed ({attendeesData.attendeeCount}/{classItem.capacity})
                    </Badge>
                  }
                />
                <Tab
                  label={
                    <Badge badgeContent={attendeesData.waitlistCount} color="warning">
                      Waitlist ({attendeesData.waitlistCount})
                    </Badge>
                  }
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ py: 2 }}>
              {tabValue === 0 && (
                <Box>
                  {attendeesData.confirmed.length > 0 ? (
                    renderAttendeesList(attendeesData.confirmed)
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No confirmed attendees yet
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  {attendeesData.waitlist.length > 0 ? (
                    renderAttendeesList(attendeesData.waitlist)
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No one on the waitlist
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={confirmationOpen}
        disableEscapeKeyDown={true}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Booking Cancellation</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to cancel {attendeeToCancel?.userName || attendeeToCancel?.userEmail}'s booking?
          </Typography>
          
          {/* Transaction cancellation options - only show if there's a paid amount */}
          {attendeeToCancel && attendeeToCancel.paidAmount && attendeeToCancel.paidAmount > 0 && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This booking has a pending payment of <strong>${Number(attendeeToCancel.paidAmount).toFixed(2)}</strong>
                </Typography>
              </Alert>
              
              <FormControl component="fieldset">
                <FormLabel component="legend">Transaction Handling:</FormLabel>
                <RadioGroup
                  value={cancelTransactionChoice ? 'cancel' : 'charge'}
                  onChange={(e) => setCancelTransactionChoice(e.target.value === 'cancel')}
                >
                  <FormControlLabel
                    value="cancel"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">
                          <strong>Cancel Transaction</strong> - Member will not be charged
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Use this if cancelling due to club issue or as a courtesy
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="charge"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">
                          <strong>Keep Transaction Pending</strong> - Member will be charged ${Number(attendeeToCancel.paidAmount).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Use this if member requested cancellation or no-show scenario
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          )}
          
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>This action is irreversible.</strong>
            </Typography>
          </Alert>
          
          {attendeesData && attendeesData.waitlistCount > 0 && (
            <Alert severity="info">
              <Typography variant="body2">
                There {attendeesData.waitlistCount === 1 ? 'is' : 'are'} <strong>{attendeesData.waitlistCount}</strong> member{attendeesData.waitlistCount > 1 ? 's' : ''} on the waitlist. 
                The next person will be automatically promoted to a confirmed spot.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmationOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
          >
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ClassAttendeesDialog;
